/**
 * Select Component - Controller
 * =============================
 */

import type { IScope, IAugmentedJQuery, IDocumentService, ISCEService, INgModelController, ITranscludeFunction } from 'angular';
import { SIZES, BASE_CLASSES, CHEVRON_ICON, DEFAULTS, SelectOption, SelectSize } from './constants';

export interface SelectBindings {
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  options?: SelectOption[];
  helpMessage?: string;
  error?: boolean;
  disabled?: boolean;
  size?: SelectSize;
  onChange?: (args: { value: string }) => void;
}

export class SelectController implements SelectBindings {
  static $inject = ['$scope', '$element', '$document', '$sce', '$transclude'];

  private $scope: IScope | null;
  private $element: IAugmentedJQuery | null;
  private $document: IDocumentService | null;
  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  ngModelCtrl?: INgModelController | null;

  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  options?: SelectOption[];
  helpMessage?: string;
  error?: boolean;
  disabled?: boolean;
  size?: SelectSize;
  onChange?: (args: { value: string }) => void;

  open = false;
  selectedValue: string | null = null;
  trustedChevron: unknown = null;
  labelId = '';

  // Slots nombrados (label/help): detectados con isSlotFilled, que SÍ funciona
  // para multi-slot transclusion (a diferencia del slot default booleano).
  hasLabelSlot = false;
  hasHelpSlot = false;

  // Opciones registradas dinámicamente por hijos <ec-select-option>.
  registeredOptions: SelectOption[] = [];

  private _onDocClick: (e: Event) => void;

  constructor(
    $scope: IScope,
    $element: IAugmentedJQuery,
    $document: IDocumentService,
    $sce: ISCEService,
    $transclude: ITranscludeFunction
  ) {
    this.$scope = $scope;
    this.$element = $element;
    this.$document = $document;
    this.$sce = $sce;
    this.$transclude = $transclude;

    this._onDocClick = (e: Event): void => {
      if (this.open && this.$element && !this.$element[0].contains(e.target as Node)) {
        if (this.$scope) {
          this.$scope.$applyAsync(() => { this.open = false; });
        }
      }
    };
  }

  $onInit(): void {
    this.size = this.size || DEFAULTS.size;
    const uid = this.$scope ? String(this.$scope.$id) : Math.random().toString(36).slice(2);
    this.labelId = 'ec-select-label-' + uid;
    if (this.$sce) {
      this.trustedChevron = this.$sce.trustAsHtml(CHEVRON_ICON);
    }
    if (this.$transclude) {
      this.hasLabelSlot = this.$transclude.isSlotFilled('labelSlot');
      this.hasHelpSlot = this.$transclude.isSlotFilled('helpSlot');
    }
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$render = () => {
        const v = this.ngModelCtrl ? this.ngModelCtrl.$viewValue : null;
        this.selectedValue = (v != null && v !== '') ? String(v) : null;
      };
    }
    if (this.$document) {
      // Listener en fase capture (3er arg true): corre ANTES de que el click llegue al
      // trigger. Al abrir, `open` todavía es false, así que el guard en _onDocClick evita
      // el auto-cierre. jqLite .on() no soporta capture, por eso addEventListener nativo.
      (this.$document[0] as Document).addEventListener('click', this._onDocClick, true);
    }
  }

  $onDestroy(): void {
    if (this.$document) {
      (this.$document[0] as Document).removeEventListener('click', this._onDocClick, true);
    }
    this.$scope = null;
    this.$element = null;
    this.$document = null;
    this.$sce = null;
    this.$transclude = null;
    this.ngModelCtrl = null;
  }

  /**
   * Registro dinámico de opciones vía hijos `<ec-select-option>`.
   * Se llama desde `SelectOptionController.$onInit` (con guard de padre).
   */
  registerOption(option: SelectOption): void {
    this.registeredOptions.push(option);
  }

  /**
   * Deregistro por value — se llama desde `SelectOptionController.$onDestroy`.
   * Los valores se asumen únicos (igual que el track by del ng-repeat).
   */
  deregisterOption(value: string): void {
    this.registeredOptions = this.registeredOptions.filter(o => o.value !== value);
  }

  /**
   * Opciones efectivas: registradas (compound) tienen prioridad sobre el
   * binding `options` (legacy). Debe ser getter (no snapshot) porque los
   * hijos transcluidos linkean DESPUÉS del $onInit del padre.
   */
  get effectiveOptions(): SelectOption[] {
    return this.registeredOptions.length ? this.registeredOptions : (this.options || []);
  }

  toggle($event: Event): void {
    if (this.disabled) return;
    $event.stopPropagation();
    this.open = !this.open;
  }

  selectOption(opt: SelectOption, $event: Event): void {
    if (opt.disabled) return;
    $event.stopPropagation();
    this.selectedValue = opt.value;
    this.open = false;
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue(opt.value);
    }
    if (typeof this.onChange === 'function') {
      this.onChange({ value: opt.value });
    }
  }

  handleKeydown($event: KeyboardEvent): void {
    if ($event.key === 'Escape') {
      this.open = false;
    }
  }

  getWrapperClasses(): string[] { return [BASE_CLASSES.wrapper]; }
  getRelativeClasses(): string[] { return [BASE_CLASSES.relative]; }

  getTriggerClasses(): string[] {
    const classes = [BASE_CLASSES.trigger, SIZES[this.size || DEFAULTS.size].trigger, SIZES[this.size || DEFAULTS.size].text];
    if (this.disabled) classes.push(BASE_CLASSES.triggerDisabled);
    return classes;
  }

  // Error (patrón field §1): Figma no pinta borde — el color va en el texto del valor.
  // Colores mutuamente excluyentes (nunca combinar triggerTextColor + triggerTextError).
  getTriggerTextClasses(): string[] {
    return this.error
      ? [BASE_CLASSES.triggerText, BASE_CLASSES.triggerTextError]
      : [BASE_CLASSES.triggerText, BASE_CLASSES.triggerTextColor];
  }

  getPlaceholderClasses(): string[] { return [BASE_CLASSES.triggerText, BASE_CLASSES.triggerPlaceholder]; }

  getChevronClasses(): string[] {
    const classes = [BASE_CLASSES.chevron];
    if (this.open) classes.push(BASE_CLASSES.chevronOpen);
    return classes;
  }

  getPanelClasses(): string[] { return [BASE_CLASSES.panel]; }

  getOptionClasses(opt: SelectOption): string[] {
    const classes = [BASE_CLASSES.option, SIZES[this.size || DEFAULTS.size].option];
    if (opt.value === this.selectedValue) classes.push(BASE_CLASSES.optionSelected);
    if (opt.disabled) classes.push(BASE_CLASSES.optionDisabled);
    return classes;
  }

  getLabelClasses(): string[] { return [BASE_CLASSES.label]; }
  getHelpClasses(): string[] {
    return this.error
      ? [BASE_CLASSES.help, BASE_CLASSES.helpError]
      : [BASE_CLASSES.help, BASE_CLASSES.helpColor];
  }

  get selectedOption(): SelectOption | null {
    if (this.selectedValue == null) return null;
    const found = this.effectiveOptions.find(o => o.value === this.selectedValue);
    return found || null;
  }
}

/**
 * Hijo de configuración `ecSelectOption` (como `<option>` nativo).
 * `template: ''` — no renderiza nada visible; solo registra su data en el
 * padre. require OPCIONAL (`^?`) + guard, por convención transversal del
 * patrón compound (spec §1.1): un require obligatorio lanzaría
 * `$compile:ctreq` si el hijo se compila/testea alguna vez sin un
 * `<ec-select>` ancestro real; con `^?` simplemente no registra.
 */
export class SelectOptionController {
  static $inject = [];

  select?: SelectController;
  value?: string;
  label?: string;
  disabled?: boolean;

  $onInit(): void {
    if (this.select) {
      this.select.registerOption({
        value: this.value as string,
        label: this.label as string,
        disabled: !!this.disabled
      });
    }
  }

  $onDestroy(): void {
    if (this.select && this.value != null) {
      this.select.deregisterOption(this.value);
    }
  }
}

export default SelectController;
